import React, { FC, useEffect, useMemo, useState } from "react";
import { _throw } from "shared-ts-utils-core";
import { IconButton, InputAdornment, TextField, Tooltip } from "@mui/material";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import { RemoveButton } from "./RemoveButton";
import { usePropertyEditBase } from "./usePropertyEditBase";
import { useSxMerge } from "shared-ts-mui";
import { PropertyEditBaseProps } from "./PropertyEditBaseProps";
import { UnitSelect } from "./UnitSelect";

export interface PropertyEditDurationProps extends PropertyEditBaseProps {
  // Target unit for the underlying value passed to onChange/onBlur, e.g. "sec"
  unit: string; // required for correct conversion
  onUnitChange?: (value: string) => void;
  autoFocus?: boolean;
  helperText?: React.ReactNode;
  error?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
}

type DurationUnit = "ms" | "sec" | "min" | "hour" | "day" | "month" | "year";

const durationUnits: DurationUnit[] = [
  "ms",
  "sec",
  "min",
  "hour",
  "day",
  "month",
  "year",
];

const unitToMillis = (unit: DurationUnit): number => {
  switch (unit) {
    case "ms":
      return 1;
    case "sec":
      return 1000;
    case "min":
      return 60 * 1000;
    case "hour":
      return 60 * 60 * 1000;
    case "day":
      return 24 * 60 * 60 * 1000;
    case "month":
      // Assumption: 30 days per month
      return 30 * 24 * 60 * 60 * 1000;
    case "year":
      // Assumption: 365 days per year
      return 365 * 24 * 60 * 60 * 1000;
    default:
      return _throw(new Error(`Unsupported unit: ${unit}`));
  }
};

const normalizeToLargestIntegralUnit = (
  valueInTargetUnit: number | null | undefined,
  targetUnit: DurationUnit
): { displayValue: number; displayUnit: DurationUnit } => {
  if (valueInTargetUnit == null)
    return { displayValue: 0, displayUnit: targetUnit };
  const targetMillis = unitToMillis(targetUnit);
  const millis = valueInTargetUnit * targetMillis;

  // Try from largest to smallest for integral division
  for (let i = durationUnits.length - 1; i >= 0; i--) {
    const u = durationUnits[i];
    const m = unitToMillis(u);
    if (millis % m === 0) {
      return { displayValue: millis / m, displayUnit: u };
    }
  }
  // Fallback: keep target unit
  return { displayValue: valueInTargetUnit, displayUnit: targetUnit };
};

const coerceToDurationUnit = (u: string): DurationUnit => {
  const normalized = u.toLowerCase();
  if (
    normalized === "ms" ||
    normalized === "millisecond" ||
    normalized === "milliseconds"
  )
    return "ms";
  if (
    normalized === "s" ||
    normalized === "sec" ||
    normalized === "second" ||
    normalized === "seconds"
  )
    return "sec";
  if (
    normalized === "m" ||
    normalized === "min" ||
    normalized === "minute" ||
    normalized === "minutes"
  )
    return "min";
  if (
    normalized === "h" ||
    normalized === "hr" ||
    normalized === "hour" ||
    normalized === "hours"
  )
    return "hour";
  if (normalized === "d" || normalized === "day" || normalized === "days")
    return "day";
  if (
    normalized === "mo" ||
    normalized === "mon" ||
    normalized === "month" ||
    normalized === "months"
  )
    return "month";
  if (
    normalized === "y" ||
    normalized === "yr" ||
    normalized === "year" ||
    normalized === "years"
  )
    return "year";
  return _throw(new Error(`Unsupported duration unit: ${u}`));
};

export const PropertyEditDuration: FC<PropertyEditDurationProps> = (props) => {
  const { removable, handleRemove } = usePropertyEditBase(props);

  const targetUnit = useMemo<DurationUnit>(
    () => coerceToDurationUnit(props.unit),
    [props.unit]
  );

  const initial = useMemo(
    () =>
      normalizeToLargestIntegralUnit(
        props.value as number | null | undefined,
        targetUnit
      ),
    [props.value, targetUnit]
  );
  const [displayUnit, setDisplayUnit] = useState<DurationUnit>(
    initial.displayUnit
  );
  const [displayValue, setDisplayValue] = useState<number>(
    initial.displayValue
  );

  useEffect(() => {
    const n = normalizeToLargestIntegralUnit(
      props.value as number | null | undefined,
      targetUnit
    );
    setDisplayUnit(n.displayUnit);
    setDisplayValue(n.displayValue);
  }, [props.value, targetUnit]);

  const sx = useSxMerge(
    {
      width: props.fullWidth !== true ? "25ch" : undefined,
      ".MuiInputBase-root": {
        pr: removable ? 1 : undefined,
      },
    },
    props.sx
  );

  const fireChange = (val: number | null, unitForVal: DurationUnit) => {
    // Convert from displayed value/unit back to target unit, then call onChange
    if (val == null) {
      props.onChange(
        null,
        props.name ?? _throw(new Error("name cannot be null"))
      );
      return;
    }
    const millis = val * unitToMillis(unitForVal);
    const target = millis / unitToMillis(targetUnit);
    props.onChange(
      target,
      props.name ?? _throw(new Error("name cannot be null"))
    );
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (v === "") {
      setDisplayValue(0);
      fireChange(null, displayUnit);
      return;
    }
    const n = Number(v);
    setDisplayValue(n);
    fireChange(n, displayUnit);
  };

  const handleUnitChange = (unitStr: string) => {
    const u = coerceToDurationUnit(unitStr);
    setDisplayUnit(u);
    props.onUnitChange?.(u);
    // Re-fire change with the same displayValue but new unit
    fireChange(displayValue, u);
  };

  return (
    <TextField
      className={"Property"}
      autoFocus={props.autoFocus}
      label={props.label}
      name={props.name ?? _throw(new Error("name cannot be null"))}
      value={Number.isFinite(displayValue) ? displayValue : 0}
      size={props.size}
      type={"number"}
      onChange={handleNumberChange}
      onBlur={(e) => props.onBlur?.(e)}
      fullWidth={props.fullWidth}
      inputRef={props.inputRef}
      helperText={props.helperText}
      error={props.error}
      sx={sx}
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <UnitSelect
                selected={displayUnit}
                options={durationUnits}
                onChange={handleUnitChange}
                tabbable={false}
              />
              {props.info && (
                <Tooltip title={props.info}>
                  <IconButton size={"small"}>
                    <HelpOutlineRoundedIcon />
                  </IconButton>
                </Tooltip>
              )}
              {removable && <RemoveButton onRemove={handleRemove} />}
            </InputAdornment>
          ),
        },
      }}
    />
  );
};
